import { useCallback } from "react";
import { Bell, LogOut, Menu, User } from "lucide-react";

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
} from "@/shared/ui";
import { Logo } from "@/shared/ui/brand/Logo";
import { GlobalSearch } from "@/features/global-search";
import { getSourceLabel, logoutUser } from "@/lib/authHelpers";
import type { SessionUser } from "@/lib/session";
import type { Source } from "@/types";

import { useSeatName } from "./useSeatName";

interface TopbarProps {
  scope?: Source;
  user?: SessionUser;
  isMobile?: boolean;
  /** Opens the mobile drawer on mobile, toggles the desktop rail otherwise. */
  onToggle: () => void;
}

/**
 * Application header — fills the AppShell grid's full-width header row. Left:
 * menu toggle + brand mark. Right: context badge, notifications, and the
 * account menu (Profile / Logout).
 */
export function Topbar({ scope, user, isMobile, onToggle }: TopbarProps) {
  const derivedSource = user?.scope ?? scope;
  const displayLabel =
    getSourceLabel(derivedSource) ?? (derivedSource ? derivedSource.toUpperCase() : "Context");

  // The badge reads "<level>: <who>", so pairing "Employer" with the merchant
  // code named the wrong thing: the tenant, not the employer signed in. Fall
  // back to the code only for seats with no node to name (platform), and until
  // the lookup answers.
  const seatName = useSeatName();
  const displayMerchant = seatName ?? user?.merchantId ?? "—";

  const handleLogout = useCallback(() => {
    void logoutUser(user?.username);
  }, [user?.username]);

  return (
    <div className="flex h-full items-center justify-between border-b border-border bg-card px-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          onClick={onToggle}
          aria-label={isMobile ? "Open navigation menu" : "Toggle sidebar"}
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </Button>

        <div className="flex items-center gap-2">
          <Logo height={30} />
          <Separator orientation="vertical" className="h-6" />
        </div>
      </div>

      {/* Global search — hidden on mobile where the drawer covers navigation. */}
      <div className="mx-4 hidden flex-1 justify-center md:flex">
        <GlobalSearch />
      </div>

      <div className="flex items-center gap-3">
        <Badge
          variant="secondary"
          className="mr-1 hidden rounded-full font-semibold sm:inline-flex"
        >
          {displayLabel}: {displayMerchant}
        </Badge>

        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Notifications">
          <Bell className="h-5 w-5" aria-hidden="true" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full"
              aria-label="Account menu"
            >
              <User className="h-5 w-5" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel className="hidden text-muted-foreground sm:block">
              {user?.merchantId || "My Account"}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="hidden sm:block" />
            <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground sm:hidden">
              {displayLabel}: {displayMerchant}
            </div>
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
